import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap, take, startWith, pairwise, filter } from 'rxjs/operators';
import { addDecimals, toFixedFloat, toPriceString } from '../../utils/helper.functions';

const allowedKeys = [
    'Backspace',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    'Home',
    'End',
    'Tab',
    '.',
    ',',
    'Unidentified',
];

type SeparatorOptions = 'comma' | 'dot';
type FormControlTypes = number | string | null;

@Directive({
    selector: 'input[appPriceInput]',
})
export class PriceInputDirective implements ControlValueAccessor, OnInit, OnDestroy {
    @Input() public outputType: 'string' | 'number' = 'number';
    @Input() public separator: SeparatorOptions = 'comma';

    private onChange: (val: FormControlTypes) => void;
    private onTouched: () => void;

    private unsubscribe$: Subject<void> = new Subject<void>();
    private innerValue$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    public constructor(
        private ngControl: NgControl,
        private readonly elementRef: ElementRef,
    ) {
        this.ngControl.valueAccessor = this;
        const el = this.elementRef.nativeElement as HTMLInputElement;
        el.inputMode = 'decimal';
        el.type = 'text';
        el.pattern = '';
    }

    public ngOnInit(): void {
        this.subscribeForValueChanges();
        this.subscribeForInputEvent();
        this.subscribeForKeyDownEvent();
        this.subscribeForBlurEvent();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public writeValue(val: FormControlTypes): void {
        if (typeof val === 'number' || typeof val === 'string' || val === null) {
            this.innerValue$.next(toPriceString(val, 2, this.getSeparator()));
        }
    }

    public registerOnChange(fn: (val: FormControlTypes) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    private subscribeForValueChanges(): void {
        this.innerValue$
            .pipe(
                startWith(this.innerValue$.value),
                tap((val) => {
                    this.updateInputEl(val);
                }),
                pairwise(),
                filter(([prev, next]) => prev !== next),
                map(([, next]) => next),
                map((val) => (this.outputType === 'number' ? toFixedFloat(val) : this.sanitizeOutput(val))),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((val: number | string) => {
                if (typeof this.onChange === 'function') {
                    this.onChange(val);
                }
            });
    }

    private subscribeForInputEvent(): void {
        fromEvent(this.elementRef.nativeElement, 'input')
            .pipe(
                map((e: InputEvent) => e.target as HTMLInputElement),
                map((target: HTMLInputElement) =>
                    // Samsungs numeric keyboard has a long standing bug and disables the comma
                    // key. As a workaround we allow both separators, but convert them
                    // to the chosen separator after input:
                    target.value.replace(/[.,]/gm, this.getSeparator()),
                ),
                map((value: string) => {
                    // Android soft keyboard sends 'Unidentified' with some keydown events instead
                    // of the propper key value, so just preventing the keydown event does not work
                    // in all cases.
                    // To be sure only numbers and separators can be entered we need to strip everything
                    // else from the inputs value. On the plus side entering values via copy and paste
                    // will now keep only numbers and separators, so no extra validation is necessary.
                    const allButNumbersAndSeparatorRegExp = new RegExp(`[^0-9${this.getSeparator(true)}]`);
                    return value.replace(allButNumbersAndSeparatorRegExp, '') || '';
                }),
                takeUntil(this.unsubscribe$),
            )
            .subscribe(this.innerValue$);
    }

    private subscribeForKeyDownEvent(): void {
        fromEvent(this.elementRef.nativeElement, 'keydown')
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((e: KeyboardEvent) => {
                if (!/^[0-9]$/.test(e.key) && !allowedKeys.includes(e.key) && e.ctrlKey !== true) {
                    e.preventDefault();
                }
            });
    }

    private subscribeForBlurEvent(): void {
        fromEvent(this.elementRef.nativeElement, 'blur')
            .pipe(
                tap(() => {
                    if (typeof this.onTouched === 'function') {
                        this.onTouched();
                    }
                }),
                switchMap(() =>
                    this.innerValue$.pipe(
                        take(1),
                        map((value) => this.sanitizeOutput(value)),
                    ),
                ),
                takeUntil(this.unsubscribe$),
            )
            .subscribe(this.innerValue$);
    }

    private sanitizeInput(value: string): string {
        const escapedSeparator = this.getSeparator(true);
        const tooManyDecimalSeparatorsRegExp = new RegExp(
            `([0-9]*\\${escapedSeparator}[0-9]*)(\\${escapedSeparator}[0-9]*)*`,
        );

        value = value.replace(tooManyDecimalSeparatorsRegExp, '$1');
        const i = value.indexOf(this.getSeparator());

        if (i !== -1) {
            value = value.substring(0, i + 3);
        }

        return value;
    }

    private sanitizeOutput(value: string): string {
        if (!value) {
            return '';
        }
        const separator = this.getSeparator();
        const leadingZerosRegExp = new RegExp('^0+([0-9])');

        value = this.sanitizeInput(value);
        value = addDecimals(value, 2, separator);
        value = value.replace(leadingZerosRegExp, '$1');

        return value;
    }

    private updateInputEl(value: string): void {
        const el = this.elementRef.nativeElement as HTMLInputElement;
        const selectionStart = el.selectionStart;
        el.value = this.sanitizeInput(value);
        // Safari focuses the input when selectionStart is set, so we don't do this when the element
        // is not already focused. Otherwise Safari users will always get refocused when they try to
        // foccus another element.
        // @see https://bugs.webkit.org/show_bug.cgi?id=224425
        if (this.elementRef.nativeElement === document.activeElement) {
            // reset the selection to the position before sanitization:
            el.selectionStart = el.selectionEnd = selectionStart - Math.max(value.length - el.value.length, 0);
        }
    }

    private getSeparator(escape: boolean = false): string {
        return this.separator === 'comma' ? ',' : escape ? '\\.' : '.';
    }
}
